import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { trackMiniAppEvent } from '../../utils/analytics';
import {
  getEngineRootStyle,
  titleStyle,
  instructionsStyle,
  secondaryBtn,
  tapRoundNeutralChoiceBtn,
  feedbackCorrect,
  feedbackIncorrect,
} from './shared/engineTheme';
import { useCelebration, CelebrationOverlay, A11yAnnouncement } from './shared/Celebration';
import { useTouchFirstPlacement } from './shared/useTouchFirstPlacement';
import { useGuidedHints } from './shared/useGuidedHints';
import GuidedHintOverlay, {
  GuidedHintHelpButton,
  guidedHintReadAloud,
} from './shared/GuidedHintOverlay';
import { buildOneShotInteractionHint, buildMatchTapOneShotHint } from './shared/dragDropMechanicHints';
import { viewportPointerFromDragEnd } from './shared/viewportPointerFromDrag';
import { isTapFirstLessonUi } from './shared/lessonUiMode';
import { shouldSuppressActivityTitle } from './shared/guidedPromptUtils';
import {
  buildMatchTapRoundCorrectFeedback,
  buildNaturalTapRoundPrompt,
  inferNaturalClassificationQuestion,
  pickPrimaryVisiblePrompt,
  splitKidFriendlyChoiceLabel,
} from './shared/guidedTapRoundUi';
import { getGuidedRoundNextButtonLabel } from './shared/guidedRoundLabels';

const TAP_CORRECT = 'Nice match!';
const TAP_WRONG = 'Not quite — try another one.';
const TAP_PICK_ROUND_FIRST = 'Pick one first, then tap where it goes.';

/**
 * Normalize legacy and alternate JSON shapes to `{ targets, draggables }`.
 * Primary schema: `targets: [{ id, label }], draggables: [{ id, text, matchesTargetId }]`.
 */
function normalizeMatchContent(content) {
  const c = content && typeof content === 'object' && !Array.isArray(content) ? content : {};
  const t0 = Array.isArray(c.targets) ? c.targets : [];
  const d0 = Array.isArray(c.draggables) ? c.draggables : [];
  if (t0.length && d0.length) {
    return { targets: t0, draggables: d0 };
  }
  if (Array.isArray(c.pairs) && c.pairs.length) {
    const targets = [];
    const draggables = [];
    const seenT = new Set();
    c.pairs.forEach((p, i) => {
      const tid = String(p.targetId ?? p.rightId ?? p.matchesTargetId ?? `target-${i}`);
      const cid = String(p.clueId ?? p.id ?? `clue-${i}`);
      const label = String(p.targetLabel ?? p.target ?? p.right ?? p.label ?? `Option ${i + 1}`);
      const text = String(p.clueText ?? p.clue ?? p.left ?? p.text ?? '');
      const matchTo = String(p.matchesTargetId ?? p.targetId ?? tid);
      if (!seenT.has(tid)) {
        seenT.add(tid);
        targets.push({ id: tid, label });
      }
      draggables.push({ id: cid, text, matchesTargetId: matchTo });
    });
    return { targets, draggables };
  }
  return { targets: [], draggables: [] };
}

function shuffleArray(arr) {
  const copy = [...(arr || [])];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** Up to `maxChoices` targets including the correct id, shuffled (mobile-friendly). */
function pickGuidedRoundTargets(allTargets, correctId, maxChoices) {
  if (!Array.isArray(allTargets) || allTargets.length === 0) return [];
  if (allTargets.length <= maxChoices) return shuffleArray(allTargets);
  const cid = String(correctId ?? '');
  const correct = allTargets.find((t) => String(t.id) === cid);
  if (!correct) return shuffleArray(allTargets);
  const wrongPool = shuffleArray(allTargets.filter((t) => String(t.id) !== cid));
  const needWrong = maxChoices - 1;
  return shuffleArray([correct, ...wrongPool.slice(0, needWrong)]);
}

function useMinWidthLayout(minPx = 640) {
  const [wide, setWide] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(`(min-width: ${minPx}px)`).matches : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(`(min-width: ${minPx}px)`);
    const on = () => setWide(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [minPx]);
  return wide;
}

/**
 * Match clues to habitats (tap-only by default). Guided lessons: one clue at a time, tap a habitat
 * option (all targets shown as large buttons, shuffled each round). Legacy drag mode: `enableDragMode={true}`.
 *
 * Content: `{ instructions?, targets: [{id,label}], draggables: [{id,text,matchesTargetId}] }`
 * Alternate: `pairs: [{ clue, target / left, right, matchesTargetId, targetId, clueId, ... }]`.
 *
 * onComplete: { activityId, engine: 'match', perfect: boolean, misses: number }
 */
export default function MatchEngine({
  title = 'Match together',
  content = {},
  hints = [],
  explanation,
  activityId = 'engine_match',
  onProgress,
  onComplete,
  guidedHintsEnabled = true,
  onRegisterLessonFooterActions,
  lessonUiMode = 'default',
  /** Opt-in legacy drag-and-drop worksheet UI (chat/embed only; guided lessons never pass this). */
  enableDragMode = false,
  guidedContext,
}) {
  const singleSurface = guidedContext?.singleSurface === true;
  const stepTitle = typeof guidedContext?.stepTitle === 'string' ? guidedContext.stepTitle : '';
  const suppressActivityTitle = shouldSuppressActivityTitle({ stepTitle, activityTitle: title });

  const { targets, draggables } = useMemo(() => normalizeMatchContent(content), [content]);

  const guidedTapRoundsMode = useMemo(
    () =>
      !enableDragMode && isTapFirstLessonUi(lessonUiMode) && targets.length > 0 && draggables.length > 0,
    [enableDragMode, lessonUiMode, targets.length, draggables.length],
  );

  /** Lesson shell: pick a habitat, then footer Check (same pattern as MultipleChoiceEngine). */
  const deferredTapRoundCheck = guidedTapRoundsMode && typeof onRegisterLessonFooterActions === 'function';

  const coarseTouchFirst = useTouchFirstPlacement();
  const touchFirstLegacy = isTapFirstLessonUi(lessonUiMode) || coarseTouchFirst;

  const [matches, setMatches] = useState({});
  /** Per-clue completion when `guidedTapRoundsMode` (allows multiple clues → same habitat). */
  const [solvedClueIds, setSolvedClueIds] = useState({});
  const [selectedClueId, setSelectedClueId] = useState(null);
  const [missCount, setMissCount] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [a11yMsg, setA11yMsg] = useState('');
  const [highlightTargetId, setHighlightTargetId] = useState(null);
  const [shakeTargetId, setShakeTargetId] = useState(null);
  /** After a correct tap-round pick, hold UI until learner taps the next-round control. */
  const [matchTapHold, setMatchTapHold] = useState(null);
  /** Lesson shell: selected habitat for current clue before Check is pressed. */
  const [pendingRoundTargetId, setPendingRoundTargetId] = useState(null);
  /** Shell “Show answer” completes every pair — defer `onComplete` until Continue. */
  const [awaitContinueAfterRevealAnswer, setAwaitContinueAfterRevealAnswer] = useState(false);
  const [showCelebration, celebrate] = useCelebration();
  const wideLayout = useMinWidthLayout(640);

  const targetRefs = useRef(new Map());
  const completedRef = useRef(false);
  const feedbackClearRef = useRef(null);
  const lessonShellFooter = typeof onRegisterLessonFooterActions === 'function';

  const placedChipIds = useMemo(() => {
    if (guidedTapRoundsMode) {
      const s = new Set();
      draggables.forEach((d) => {
        if (solvedClueIds[d.id]) s.add(d.id);
      });
      return s;
    }
    return new Set(Object.values(matches));
  }, [guidedTapRoundsMode, solvedClueIds, matches, draggables]);

  const allMatched = useMemo(() => {
    if (guidedTapRoundsMode) {
      return draggables.length > 0 && draggables.every((d) => solvedClueIds[d.id]);
    }
    return (
      targets.length > 0 &&
      targets.every((t) => matches[t.id]) &&
      Object.keys(matches).length === targets.length
    );
  }, [guidedTapRoundsMode, draggables, solvedClueIds, targets, matches]);

  const currentRoundClueId = useMemo(() => {
    const c = draggables.find((d) => !solvedClueIds[d.id]);
    return c?.id ?? null;
  }, [draggables, solvedClueIds]);

  const roundTargetChoices = useMemo(() => {
    const chip = draggables.find((d) => !solvedClueIds[d.id]);
    if (!chip) return shuffleArray(targets);
    return pickGuidedRoundTargets(targets, chip.matchesTargetId, 4);
  }, [targets, draggables, solvedClueIds, currentRoundClueId]);

  const getExplanation = useCallback(() => content.explanation, [content.explanation]);

  const tapRoundClassification = useMemo(() => {
    if (!guidedTapRoundsMode) return { question: '', domain: 'group' };
    const ins = typeof content.instructions === 'string' ? content.instructions : '';
    return inferNaturalClassificationQuestion({
      targets,
      zoneLabels: [],
      stepTitle,
      activityTitle: title,
      instructions: ins,
    });
  }, [guidedTapRoundsMode, targets, stepTitle, title, content.instructions]);

  const oneShotInteractionHint = useMemo(() => {
    if (enableDragMode) {
      return buildOneShotInteractionHint(touchFirstLegacy, { canvasStyle: false });
    }
    return buildMatchTapOneShotHint(tapRoundClassification.domain);
  }, [enableDragMode, touchFirstLegacy, tapRoundClassification.domain]);

  const guided = useGuidedHints({
    activityId,
    hints,
    oneShotInteractionHint,
    explanation,
    getExplanation,
    isActivityComplete: allMatched && !awaitContinueAfterRevealAnswer,
    enabled: guidedHintsEnabled,
  });
  const { noteCorrectAnswer, noteIncorrectAnswer, noteUserAction, resetSession } = guided;

  const sortedClues = useMemo(() => {
    const open = draggables.filter((d) => !placedChipIds.has(d.id));
    const done = draggables.filter((d) => placedChipIds.has(d.id));
    return [...open, ...done];
  }, [draggables, placedChipIds]);

  const sortedHabitats = useMemo(() => {
    const open = targets.filter((t) => !matches[t.id]);
    const done = targets.filter((t) => matches[t.id]);
    return [...open, ...done];
  }, [targets, matches]);

  useEffect(() => {
    if (!allMatched || completedRef.current) return;
    if (lessonShellFooter && awaitContinueAfterRevealAnswer) return;
    completedRef.current = true;
    celebrate();
    noteCorrectAnswer();
    setFeedback({ type: 'correct', message: 'All matched!' });
    setA11yMsg('All matched!');
    onComplete?.({
      activityId,
      engine: 'match',
      perfect: missCount === 0,
      misses: missCount,
    });
    trackMiniAppEvent.completed(activityId);
  }, [
    allMatched,
    activityId,
    celebrate,
    missCount,
    noteCorrectAnswer,
    onComplete,
    lessonShellFooter,
    awaitContinueAfterRevealAnswer,
  ]);

  const clearFeedbackSoon = useCallback((ms) => {
    if (feedbackClearRef.current) window.clearTimeout(feedbackClearRef.current);
    feedbackClearRef.current = window.setTimeout(() => {
      feedbackClearRef.current = null;
      setFeedback((f) => (f?.message === 'All matched!' ? f : null));
    }, ms);
  }, []);

  useEffect(
    () => () => {
      if (feedbackClearRef.current) window.clearTimeout(feedbackClearRef.current);
    },
    [],
  );

  const place = useCallback(
    (chip, targetId, source) => {
      if (placedChipIds.has(chip.id) || matches[targetId]) return;
      const correct = chip.matchesTargetId === targetId;
      if (correct) {
        noteUserAction();
        const next = { ...matches, [targetId]: chip.id };
        setMatches(next);
        setSelectedClueId(null);
        setShakeTargetId(null);
        setHighlightTargetId(null);
        setFeedback({ type: 'correct', message: TAP_CORRECT });
        setA11yMsg(TAP_CORRECT);
        if (!enableDragMode) clearFeedbackSoon(1400);
        onProgress?.({
          activityId,
          engine: 'match',
          phase: 'pair',
          source,
          targetId,
          chipId: chip.id,
          correct: true,
        });
        trackMiniAppEvent.interaction(activityId, 'match_correct');
      } else {
        noteIncorrectAnswer();
        setMissCount((c) => c + 1);
        const targetLabel = targets.find((x) => x.id === targetId)?.label;
        const wrongMsg = enableDragMode
          ? targetLabel
            ? `${chip.text} does not belong in "${targetLabel}".`
            : `${chip.text} was not matched.`
          : TAP_WRONG;
        setFeedback({ type: 'wrong', message: wrongMsg });
        setA11yMsg(wrongMsg);
        setSelectedClueId(null);
        setShakeTargetId(targetId);
        window.setTimeout(() => setShakeTargetId((id) => (id === targetId ? null : id)), 700);
        if (!enableDragMode) {
          setHighlightTargetId(targetId);
          window.setTimeout(() => setHighlightTargetId((cur) => (cur === targetId ? null : cur)), 700);
          clearFeedbackSoon(1800);
        } else {
          setHighlightTargetId(targetId);
          setTimeout(() => setHighlightTargetId((cur) => (cur === targetId ? null : cur)), 600);
        }
        onProgress?.({
          activityId,
          engine: 'match',
          phase: 'pair',
          source,
          targetId,
          chipId: chip.id,
          correct: false,
        });
        trackMiniAppEvent.interaction(activityId, 'match_wrong');
      }
    },
    [
      matches,
      placedChipIds,
      activityId,
      noteIncorrectAnswer,
      noteUserAction,
      onProgress,
      enableDragMode,
      clearFeedbackSoon,
      targets,
    ],
  );

  const commitGuidedRoundPick = useCallback(
    (targetId) => {
      if (matchTapHold) return;
      const chip = draggables.find((d) => !solvedClueIds[d.id]);
      if (!chip) return;
      const tid = String(chip.matchesTargetId ?? '');
      const valid = targets.some((t) => t.id === tid);
      if (!valid) return;

      if (tid === targetId) {
        noteUserAction();
        const tgt = targets.find((x) => String(x.id) === String(targetId));
        const remaining = draggables.filter((d) => !solvedClueIds[d.id]);
        const isLast = remaining.length === 1;
        const msg = buildMatchTapRoundCorrectFeedback({
          chip,
          target: tgt,
          domain: tapRoundClassification.domain,
        });
        setSelectedClueId(null);
        setShakeTargetId(null);
        setHighlightTargetId(null);
        setPendingRoundTargetId(null);
        setFeedback(null);
        setMatchTapHold({ clueId: chip.id, message: msg, isLast });
        setA11yMsg(msg);
        onProgress?.({
          activityId,
          engine: 'match',
          phase: 'pair',
          source: 'tap_round',
          targetId,
          chipId: chip.id,
          correct: true,
        });
        trackMiniAppEvent.interaction(activityId, 'match_correct');
      } else {
        noteIncorrectAnswer();
        setMissCount((c) => c + 1);
        const targetLabel = targets.find((x) => x.id === targetId)?.label;
        setFeedback({
          type: 'wrong',
          message: targetLabel ? `Not quite — try another choice.` : TAP_WRONG,
        });
        setA11yMsg(TAP_WRONG);
        setSelectedClueId(null);
        setPendingRoundTargetId(null);
        setShakeTargetId(targetId);
        window.setTimeout(() => setShakeTargetId((id) => (id === targetId ? null : id)), 700);
        setHighlightTargetId(targetId);
        window.setTimeout(() => setHighlightTargetId((cur) => (cur === targetId ? null : cur)), 700);
        clearFeedbackSoon(1800);
        onProgress?.({
          activityId,
          engine: 'match',
          phase: 'pair',
          source: 'tap_round',
          targetId,
          chipId: chip.id,
          correct: false,
        });
        trackMiniAppEvent.interaction(activityId, 'match_wrong');
      }
    },
    [
      matchTapHold,
      draggables,
      solvedClueIds,
      targets,
      activityId,
      noteIncorrectAnswer,
      noteUserAction,
      onProgress,
      clearFeedbackSoon,
      tapRoundClassification.domain,
    ],
  );

  const handleGuidedRoundTargetPick = useCallback(
    (targetId) => {
      if (matchTapHold) return;
      if (deferredTapRoundCheck) {
        setPendingRoundTargetId(targetId);
        return;
      }
      commitGuidedRoundPick(targetId);
    },
    [matchTapHold, deferredTapRoundCheck, commitGuidedRoundPick],
  );

  const commitRoundPickFromFooter = useCallback(() => {
    if (!deferredTapRoundCheck || pendingRoundTargetId == null) return;
    commitGuidedRoundPick(pendingRoundTargetId);
  }, [deferredTapRoundCheck, pendingRoundTargetId, commitGuidedRoundPick]);

  const advanceMatchTapRound = useCallback(() => {
    if (!matchTapHold) return;
    const { clueId } = matchTapHold;
    setMatchTapHold(null);
    setFeedback(null);
    setPendingRoundTargetId(null);
    setSolvedClueIds((s) => ({ ...s, [clueId]: true }));
    setA11yMsg('');
  }, [matchTapHold]);

  const handleClueClick = (chip) => {
    if (placedChipIds.has(chip.id)) return;
    noteUserAction();
    setSelectedClueId((cur) => (cur === chip.id ? null : chip.id));
  };

  const handleHabitatClick = (targetId) => {
    if (matches[targetId]) return;
    if (!selectedClueId) {
      setFeedback({ type: 'wrong', message: TAP_PICK_ROUND_FIRST });
      setA11yMsg(TAP_PICK_ROUND_FIRST);
      clearFeedbackSoon(2200);
      return;
    }
    const chip = draggables.find((d) => d.id === selectedClueId);
    if (!chip) return;
    place(chip, targetId, 'tap');
  };

  const handleChipDragEnd = useCallback(
    (chip, event, info) => {
      if (placedChipIds.has(chip.id)) return;
      const { x: px, y: py } = viewportPointerFromDragEnd(event, info);
      let hit = null;
      for (const t of targets) {
        const node = targetRefs.current.get(t.id);
        if (!node) continue;
        const r = node.getBoundingClientRect();
        if (px >= r.left && px <= r.right && py >= r.top && py <= r.bottom) {
          hit = t;
          break;
        }
      }
      if (hit) {
        place(chip, hit.id, 'drag');
      } else {
        noteUserAction();
        setFeedback({ type: 'wrong', message: `Drop ${chip.text} on a target box.` });
        setA11yMsg(`Drop ${chip.text} on a target box.`);
      }
    },
    [placedChipIds, targets, place, noteUserAction],
  );

  const resetPairs = () => {
    resetSession();
    completedRef.current = false;
    setAwaitContinueAfterRevealAnswer(false);
    setMatches({});
    setSolvedClueIds({});
    setSelectedClueId(null);
    setMissCount(0);
    setFeedback(null);
    setHighlightTargetId(null);
    setShakeTargetId(null);
    setMatchTapHold(null);
    setPendingRoundTargetId(null);
  };

  const revealAnswer = useCallback(() => {
    if (allMatched) return;
    if (guidedTapRoundsMode) {
      setMatchTapHold(null);
      setPendingRoundTargetId(null);
      noteUserAction();
      if (lessonShellFooter) {
        setAwaitContinueAfterRevealAnswer(true);
        setFeedback({
          type: 'correct',
          message:
            'Here is one way to match every clue. Tap Continue below when you are ready to go on.',
        });
        setA11yMsg('Answer shown. Tap Continue when you are ready.');
      }
      setSolvedClueIds(Object.fromEntries(draggables.map((d) => [d.id, true])));
      const next = {};
      for (const d of draggables) {
        const tid = String(d.matchesTargetId ?? '');
        if (tid && !next[tid]) next[tid] = d.id;
      }
      setMatches(next);
      setSelectedClueId(null);
      setHighlightTargetId(null);
      setShakeTargetId(null);
      trackMiniAppEvent.interaction(activityId, 'reveal_answer');
      onProgress?.({ activityId, engine: 'match', phase: 'reveal_answer' });
      return;
    }
    const next = {};
    for (const t of targets) {
      const chip = draggables.find((d) => d.matchesTargetId === t.id);
      if (chip) next[t.id] = chip.id;
    }
    if (Object.keys(next).length !== targets.length) return;
    noteUserAction();
    if (lessonShellFooter) {
      setAwaitContinueAfterRevealAnswer(true);
      setFeedback({
        type: 'correct',
        message:
          'Here is one way to match every pair. Tap Continue below when you are ready to go on.',
      });
      setA11yMsg('Answer shown. Tap Continue when you are ready.');
    }
    setMatches(next);
    setSelectedClueId(null);
    setHighlightTargetId(null);
    setShakeTargetId(null);
    trackMiniAppEvent.interaction(activityId, 'reveal_answer');
    onProgress?.({ activityId, engine: 'match', phase: 'reveal_answer' });
  }, [
    allMatched,
    guidedTapRoundsMode,
    targets,
    draggables,
    noteUserAction,
    activityId,
    onProgress,
    lessonShellFooter,
  ]);

  useEffect(() => {
    if (!onRegisterLessonFooterActions) return undefined;

    if (
      lessonShellFooter &&
      targets.length > 0 &&
      draggables.length > 0 &&
      allMatched &&
      awaitContinueAfterRevealAnswer &&
      !completedRef.current
    ) {
      onRegisterLessonFooterActions({
        continueLesson: () => {
          noteUserAction();
          setAwaitContinueAfterRevealAnswer(false);
          if (completedRef.current) return;
          completedRef.current = true;
          celebrate();
          noteCorrectAnswer();
          setFeedback({ type: 'correct', message: 'All matched!' });
          setA11yMsg('All matched!');
          onComplete?.({
            activityId,
            engine: 'match',
            perfect: missCount === 0,
            misses: missCount,
          });
          trackMiniAppEvent.completed(activityId);
        },
      });
      return () => onRegisterLessonFooterActions(null);
    }

    if (targets.length === 0 || draggables.length === 0 || allMatched) {
      onRegisterLessonFooterActions(null);
      return undefined;
    }
    const showAnswer = () => revealAnswer();

    if (deferredTapRoundCheck) {
      if (matchTapHold) {
        onRegisterLessonFooterActions({
          continueLesson: () => {
            noteUserAction();
            advanceMatchTapRound();
          },
          showAnswer,
        });
        return () => onRegisterLessonFooterActions(null);
      }
      onRegisterLessonFooterActions({
        checkAnswer: commitRoundPickFromFooter,
        checkAnswerDisabled: pendingRoundTargetId == null,
        showAnswer,
      });
      return () => onRegisterLessonFooterActions(null);
    }

    onRegisterLessonFooterActions({ showAnswer });
    return () => onRegisterLessonFooterActions(null);
  }, [
    onRegisterLessonFooterActions,
    lessonShellFooter,
    targets.length,
    draggables.length,
    allMatched,
    awaitContinueAfterRevealAnswer,
    revealAnswer,
    deferredTapRoundCheck,
    matchTapHold,
    pendingRoundTargetId,
    commitRoundPickFromFooter,
    advanceMatchTapRound,
    noteUserAction,
    celebrate,
    noteCorrectAnswer,
    onComplete,
    missCount,
    activityId,
  ]);

  if (targets.length === 0 || draggables.length === 0) {
    return (
      <div style={getEngineRootStyle({ singleSurface })}>
        <p style={instructionsStyle}>No match items in this activity yet.</p>
      </div>
    );
  }

  const sectionLabel = {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--color-text-soft, var(--color-text))',
    margin: '0 0 8px',
    opacity: 0.88,
  };

  const tapShell = {
    ...getEngineRootStyle({ singleSurface, maxWidth: 520 }),
    margin: '0 auto',
    padding: '4px 10px 10px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    minHeight: 0,
    ...(guided.contentReserveStyle || {}),
  };

  const cardBase = {
    width: '100%',
    minHeight: 56,
    borderRadius: 14,
    padding: '12px 14px',
    boxSizing: 'border-box',
    textAlign: 'left',
    fontWeight: 650,
    fontSize: 16,
    lineHeight: 1.35,
    color: 'var(--color-text)',
    border: singleSurface ? '1px solid var(--color-border)' : '2px solid var(--color-border)',
    background: 'var(--color-bg)',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 4,
    touchAction: 'manipulation',
  };

  const choiceBtnTap = {
    ...tapRoundNeutralChoiceBtn,
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
  };

  const nextClueBtnStyle = {
    ...secondaryBtn,
    width: '100%',
    minHeight: 56,
    marginTop: 12,
    borderRadius: 16,
    fontWeight: 800,
    fontSize: 17,
    border: '2px solid var(--color-primary)',
    color: 'var(--color-primary)',
    background: 'rgba(139, 92, 246, 0.08)',
  };

  if (enableDragMode) {
    return (
      <div style={{ ...getEngineRootStyle({ singleSurface }), position: 'relative', ...(guided.contentReserveStyle || {}) }}>
        <CelebrationOverlay visible={showCelebration} message="Matched!" />
        <A11yAnnouncement message={a11yMsg} />
        <GuidedHintOverlay
          open={guided.open}
          isNarrow={guided.isNarrow}
          visibleLevel={guided.visibleLevel}
          maxLevel={guided.maxLevel}
          isInteractionPhase={guided.isInteractionPhase}
          anotherClueEnabled={guided.anotherClueEnabled}
          hintEntry={guided.hintEntry}
          showHowAvailable={guided.showHowAvailable}
          resolvedExplanation={guided.resolvedExplanation}
          explanationOpen={guided.explanationOpen}
          onDismiss={guided.dismissOverlay}
          onAnotherClue={guided.anotherClue}
          onReadAloud={() =>
            guidedHintReadAloud(guided.hintEntry, activityId, guided.readAloudTrackLevel)
          }
          onShowHow={guided.openExplanation}
        />
        <GuidedHintHelpButton visible={guided.showHelpButton} onClick={guided.requestHintManually} />

        <h2 style={titleStyle}>{title}</h2>
        {content.showInstructions === true && content.instructions && <p style={instructionsStyle}>{content.instructions}</p>}

        <div style={{ fontWeight: 700, marginBottom: 8, color: 'var(--color-text)' }}>Boxes</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          {targets.map((t) => {
            const chipId = matches[t.id];
            const chip = draggables.find((d) => d.id === chipId);
            const filled = !!chip;
            const wrongHi = highlightTargetId === t.id;
            return (
              <button
                key={t.id}
                type="button"
                ref={(node) => {
                  if (node) targetRefs.current.set(t.id, node);
                  else targetRefs.current.delete(t.id);
                }}
                onClick={() => {
                  if (matches[t.id]) return;
                  if (!selectedClueId) return;
                  const c = draggables.find((d) => d.id === selectedClueId);
                  if (!c) return;
                  place(c, t.id, 'tap');
                }}
                aria-label={`Target: ${t.label}${filled ? `, filled with ${chip.text}` : ''}`}
                style={{
                  ...secondaryBtn,
                  minHeight: 64,
                  borderStyle: 'dashed',
                  borderWidth: wrongHi ? 3 : 2,
                  borderColor: wrongHi
                    ? 'rgba(239, 68, 68, 0.85)'
                    : filled
                      ? 'var(--color-primary)'
                      : 'var(--color-border)',
                  background: filled
                    ? 'rgba(99, 102, 241, 0.12)'
                    : wrongHi
                      ? 'rgba(239, 68, 68, 0.08)'
                      : 'var(--color-bg)',
                  textAlign: 'left',
                  width: '100%',
                  cursor: matches[t.id] ? 'default' : 'pointer',
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4, color: 'var(--color-text)' }}>
                  {t.label}
                </div>
                <div
                  style={
                    filled
                      ? { fontWeight: 700, fontSize: '1rem' }
                      : {
                          fontSize: 11,
                          fontWeight: 400,
                          lineHeight: 1.35,
                          opacity: 0.68,
                          color: 'var(--color-text-muted, var(--color-text))',
                        }
                  }
                >
                  {filled
                    ? `✓ ${chip.text}`
                    : touchFirstLegacy
                      ? 'Tap a chip, then tap here'
                      : 'Drag a chip here, or tap a chip then tap here'}
                </div>
              </button>
            );
          })}
        </div>

        <div style={{ fontWeight: 700, marginBottom: 8, color: 'var(--color-text)' }}>Chips</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <AnimatePresence>
            {draggables
              .filter((d) => !placedChipIds.has(d.id))
              .map((d) => {
                const active = selectedClueId === d.id;
                return (
                  <motion.button
                    key={d.id}
                    type="button"
                    drag={!touchFirstLegacy}
                    dragSnapToOrigin={!touchFirstLegacy}
                    dragMomentum={false}
                    dragElastic={0.18}
                    whileDrag={touchFirstLegacy ? undefined : { scale: 1.08, zIndex: 60 }}
                    whileTap={{ scale: 1.03 }}
                    onClick={() => handleClueClick(d)}
                    onDragStart={touchFirstLegacy ? undefined : () => noteUserAction()}
                    onDragEnd={touchFirstLegacy ? undefined : (e, info) => handleChipDragEnd(d, e, info)}
                    initial={{ scale: 0.85, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.6, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 240, damping: 22 }}
                    aria-pressed={active}
                    aria-label={`Drag ${d.text}, or tap then tap a box`}
                    style={{
                      ...secondaryBtn,
                      width: 'auto',
                      flex: '1 1 140px',
                      minHeight: 48,
                      borderWidth: active ? 3 : 2,
                      borderColor: active ? 'var(--color-primary)' : 'var(--color-border)',
                      cursor: touchFirstLegacy ? 'pointer' : 'grab',
                      touchAction: touchFirstLegacy ? 'manipulation' : 'none',
                    }}
                  >
                    {d.text}
                  </motion.button>
                );
              })}
          </AnimatePresence>
        </div>

        {feedback ? (
          <p
            style={{
              ...(feedback.type === 'correct' ? feedbackCorrect : feedbackIncorrect),
              marginTop: 14,
            }}
            role="status"
          >
            {feedback.message}
          </p>
        ) : null}

        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          {Object.keys(matches).length > 0 && !allMatched ? (
            <button
              type="button"
              style={{ ...secondaryBtn, width: 'auto', flex: '0 0 auto', minHeight: 40, padding: '8px 14px' }}
              onClick={resetPairs}
            >
              Reset
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (guidedTapRoundsMode) {
    const nextChip = draggables.find((d) => !solvedClueIds[d.id]);
    const solvedCount = draggables.filter((d) => solvedClueIds[d.id]).length;
    const promptPack = nextChip
      ? buildNaturalTapRoundPrompt({
          clueText: nextChip.text,
          domain: tapRoundClassification.domain,
        })
      : { mode: 'integrated', primaryQuestion: tapRoundClassification.question || 'Which group fits?' };
    const headlinePick = pickPrimaryVisiblePrompt([
      { text: promptPack.primaryQuestion },
      { text: typeof guidedContext?.stepTitle === 'string' ? guidedContext.stepTitle : '' },
      { text: tapRoundClassification.question || '' },
    ]);
    const headline = headlinePick.primary || promptPack.primaryQuestion;
    return (
      <div style={tapShell}>
        <CelebrationOverlay visible={showCelebration} message="Matched!" />
        <A11yAnnouncement message={a11yMsg} />
        <GuidedHintOverlay
          open={guided.open}
          isNarrow={guided.isNarrow}
          visibleLevel={guided.visibleLevel}
          maxLevel={guided.maxLevel}
          isInteractionPhase={guided.isInteractionPhase}
          anotherClueEnabled={guided.anotherClueEnabled}
          hintEntry={guided.hintEntry}
          showHowAvailable={guided.showHowAvailable}
          resolvedExplanation={guided.resolvedExplanation}
          explanationOpen={guided.explanationOpen}
          onDismiss={guided.dismissOverlay}
          onAnotherClue={guided.anotherClue}
          onReadAloud={() =>
            guidedHintReadAloud(guided.hintEntry, activityId, guided.readAloudTrackLevel)
          }
          onShowHow={guided.openExplanation}
        />
        <GuidedHintHelpButton visible={guided.showHelpButton} onClick={guided.requestHintManually} />

        {allMatched ? (
          <p
            role="status"
            style={{
              margin: '10px 0 0',
              fontSize: 16,
              fontWeight: 700,
              lineHeight: 1.4,
              color: 'var(--color-text)',
              textAlign: 'center',
              padding: '12px 14px',
              borderRadius: 14,
              background: 'rgba(34, 197, 94, 0.12)',
              border: '1px solid rgba(34, 197, 94, 0.35)',
            }}
          >
            Great job! Tap Continue when you are ready.
          </p>
        ) : (
          <>
            {promptPack.mode === 'split' && promptPack.supportingClue && nextChip && !matchTapHold ? (
              <p
                style={{
                  margin: '0 0 8px',
                  fontSize: 16,
                  fontWeight: 650,
                  lineHeight: 1.45,
                  color: 'var(--color-text)',
                }}
              >
                {promptPack.supportingClue}
              </p>
            ) : null}
            <div
              style={{
                display: 'flex',
                flexDirection: singleSurface ? 'row' : 'column',
                alignItems: singleSurface ? 'flex-end' : 'stretch',
                justifyContent: 'space-between',
                gap: 8,
                marginBottom: promptPack.mode === 'split' && nextChip && !matchTapHold ? 4 : 6,
              }}
            >
              <h2
                style={{
                  ...titleStyle,
                  margin: 0,
                  fontSize: 'clamp(1.15rem, 3.8vw, 1.35rem)',
                  lineHeight: 1.2,
                  flex: '1 1 auto',
                }}
              >
                {headline}
              </h2>
              {nextChip && !matchTapHold ? (
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    opacity: 0.72,
                    whiteSpace: 'nowrap',
                    flex: '0 0 auto',
                  }}
                >
                  {solvedCount + 1} of {draggables.length}
                </span>
              ) : null}
            </div>

            {matchTapHold ? (
              <>
                <p
                  style={{
                    ...feedbackCorrect,
                    marginTop: 4,
                    marginBottom: 0,
                    fontSize: 15,
                    lineHeight: 1.45,
                    padding: '10px 12px',
                    borderRadius: 12,
                  }}
                  role="status"
                >
                  {matchTapHold.message}
                </p>
                {!deferredTapRoundCheck ? (
                  <button type="button" style={nextClueBtnStyle} onClick={advanceMatchTapRound}>
                    {getGuidedRoundNextButtonLabel({
                      engine: 'engine_match',
                      isLast: matchTapHold.isLast,
                      stepTitle,
                      activityTitle: title,
                      instruction: typeof content.instructions === 'string' ? content.instructions : '',
                    })}
                  </button>
                ) : (
                  <p
                    style={{
                      ...instructionsStyle,
                      marginTop: 10,
                      marginBottom: 0,
                      fontSize: 13,
                      opacity: 0.8,
                      textAlign: 'center',
                    }}
                  >
                    {matchTapHold.isLast
                      ? 'Tap Continue below when you are ready.'
                      : 'Tap Continue below for the next clue.'}
                  </p>
                )}
              </>
            ) : nextChip ? (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} role="group" aria-label="Choices">
                  {roundTargetChoices.map((t) => {
                    const shaking = shakeTargetId === t.id;
                    const wrongFlash = highlightTargetId === t.id;
                    const pendingPick =
                      deferredTapRoundCheck && !matchTapHold && pendingRoundTargetId === t.id;
                    const { primary, secondary } = splitKidFriendlyChoiceLabel(t.label);
                    return (
                      <motion.button
                        key={t.id}
                        type="button"
                        onClick={() => handleGuidedRoundTargetPick(t.id)}
                        aria-label={`Choose ${primary}`}
                        animate={shaking ? { x: [0, -7, 7, -5, 5, 0] } : false}
                        transition={shaking ? { duration: 0.45 } : undefined}
                        style={{
                          ...choiceBtnTap,
                          cursor: 'pointer',
                          borderColor: wrongFlash
                            ? 'rgba(239, 68, 68, 0.85)'
                            : pendingPick
                              ? 'rgba(139, 92, 246, 0.65)'
                              : 'var(--color-border)',
                          borderWidth: wrongFlash || pendingPick ? 3 : 2,
                          background: wrongFlash
                            ? 'rgba(239, 68, 68, 0.08)'
                            : pendingPick
                              ? 'rgba(139, 92, 246, 0.12)'
                              : 'var(--color-bg)',
                        }}
                      >
                        <span style={{ fontWeight: 800, fontSize: 17 }}>{primary}</span>
                        {secondary ? (
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              opacity: 0.72,
                              textTransform: 'none',
                            }}
                          >
                            {secondary}
                          </span>
                        ) : null}
                      </motion.button>
                    );
                  })}
                </div>
              </>
            ) : null}

            {feedback && feedback.type === 'wrong' ? (
              <p
                style={{
                  ...feedbackIncorrect,
                  marginTop: 10,
                  marginBottom: 0,
                  fontSize: 14,
                  padding: '8px 10px',
                  borderRadius: 12,
                }}
                role="status"
              >
                {feedback.message}
              </p>
            ) : null}

            {(Object.keys(solvedClueIds).length > 0 || missCount > 0) && !allMatched && !matchTapHold && !deferredTapRoundCheck ? (
              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  style={{
                    ...secondaryBtn,
                    width: '100%',
                    minHeight: 48,
                    fontWeight: 700,
                  }}
                  onClick={resetPairs}
                >
                  Start over
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    );
  }

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: wideLayout ? '1fr 1fr' : '1fr',
    gap: wideLayout ? 16 : 12,
    alignItems: 'start',
    alignContent: 'start',
    flex: 1,
    width: '100%',
    maxWidth: '100%',
  };

  return (
    <div style={tapShell}>
      <CelebrationOverlay visible={showCelebration} message="Matched!" />
      <A11yAnnouncement message={a11yMsg} />
      <GuidedHintOverlay
        open={guided.open}
        isNarrow={guided.isNarrow}
        visibleLevel={guided.visibleLevel}
        maxLevel={guided.maxLevel}
        isInteractionPhase={guided.isInteractionPhase}
        anotherClueEnabled={guided.anotherClueEnabled}
        hintEntry={guided.hintEntry}
        showHowAvailable={guided.showHowAvailable}
        resolvedExplanation={guided.resolvedExplanation}
        explanationOpen={guided.explanationOpen}
        onDismiss={guided.dismissOverlay}
        onAnotherClue={guided.anotherClue}
        onReadAloud={() =>
          guidedHintReadAloud(guided.hintEntry, activityId, guided.readAloudTrackLevel)
        }
        onShowHow={guided.openExplanation}
      />
      <GuidedHintHelpButton visible={guided.showHelpButton} onClick={guided.requestHintManually} />

      {!suppressActivityTitle ? <h2 style={{ ...titleStyle, marginBottom: 8 }}>{title}</h2> : null}

      <div style={gridStyle}>
        <div style={{ minWidth: 0 }}>
          <div style={sectionLabel}>{content.draggablesLabel || 'Clues'}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sortedClues.map((d) => {
              const matched = placedChipIds.has(d.id);
              const active = selectedClueId === d.id && !matched;
              return (
                <button
                  key={d.id}
                  type="button"
                  disabled={matched}
                  onClick={() => !matched && handleClueClick(d)}
                  aria-pressed={active}
                  aria-label={matched ? `Matched clue: ${d.text}` : `Clue: ${d.text}`}
                  style={{
                    ...cardBase,
                    opacity: matched ? 0.72 : 1,
                    cursor: matched ? 'default' : 'pointer',
                    borderColor: active ? 'var(--color-primary)' : matched ? 'rgba(34, 197, 94, 0.55)' : 'var(--color-border)',
                    borderWidth: active || matched ? 3 : 2,
                    background: active
                      ? 'rgba(99, 102, 241, 0.12)'
                      : matched
                        ? 'rgba(34, 197, 94, 0.1)'
                        : 'var(--color-bg)',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                    {matched ? (
                      <span aria-hidden style={{ fontSize: 20, lineHeight: 1 }}>
                        ✓
                      </span>
                    ) : null}
                    <span>{d.text}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ minWidth: 0 }}>
          <div style={sectionLabel}>{content.targetsLabel || 'Terms'}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sortedHabitats.map((t) => {
              const chipId = matches[t.id];
              const chip = chipId ? draggables.find((d) => d.id === chipId) : null;
              const matched = !!chip;
              const shaking = shakeTargetId === t.id;
              const wrongFlash = highlightTargetId === t.id;
              return (
                <motion.button
                  key={t.id}
                  type="button"
                  disabled={matched}
                  onClick={() => !matched && handleHabitatClick(t.id)}
                  aria-label={
                    matched ? `Matched: ${t.label}` : `${t.label} — tap after choosing a clue`
                  }
                  animate={shaking ? { x: [0, -7, 7, -5, 5, 0] } : false}
                  transition={shaking ? { duration: 0.45 } : undefined}
                  style={{
                    ...cardBase,
                    opacity: matched ? 0.72 : 1,
                    cursor: matched ? 'default' : 'pointer',
                    borderColor: wrongFlash
                      ? 'rgba(239, 68, 68, 0.85)'
                      : matched
                        ? 'rgba(34, 197, 94, 0.55)'
                        : 'var(--color-border)',
                    borderWidth: wrongFlash || matched ? 3 : 2,
                    background: wrongFlash
                      ? 'rgba(239, 68, 68, 0.08)'
                      : matched
                        ? 'rgba(34, 197, 94, 0.1)'
                        : 'var(--color-bg)',
                  }}
                >
                  <span style={{ fontWeight: 800, fontSize: 16 }}>{t.label}</span>
                  {matched && chip ? (
                    <span style={{ fontSize: 14, fontWeight: 600, opacity: 0.88, display: 'flex', gap: 6 }}>
                      <span aria-hidden>✓</span>
                      <span>{chip.text}</span>
                    </span>
                  ) : null}
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {feedback && feedback.message !== 'All matched!' ? (
        <p
          style={{
            ...(feedback.type === 'correct' ? feedbackCorrect : feedbackIncorrect),
            marginTop: 10,
            marginBottom: 0,
            fontSize: 14,
            padding: '8px 10px',
            borderRadius: 12,
            maxHeight: '4.5em',
            overflow: 'hidden',
          }}
          role="status"
        >
          {feedback.message}
        </p>
      ) : null}

      {Object.keys(matches).length > 0 && !allMatched ? (
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            style={{
              ...secondaryBtn,
              width: '100%',
              minHeight: 48,
              fontWeight: 700,
            }}
            onClick={resetPairs}
          >
            Start over
          </button>
        </div>
      ) : null}
    </div>
  );
}
